import { DATA_VERSION } from 'data-migrations/dataVersions';
import type {
  MigrationFunction,
  MigrationObject,
} from 'data-migrations/types/migration';
import { BreakerApplication } from 'unitData/Breaker/BreakerTypes';
import type {
  AttachementTool,
  UnitInfoBananaScreenInterface,
  UnitInfoCMScreenInterface,
  UnitInfoCrusherInterface,
  UnitInfoFeedInterface,
  UnitInfoFreeTextBoxInterface,
  UnitInfoGenericCrusherInterface,
  UnitInfoGenericScreenInterface,
  UnitInfoGrizzlyScreenSGInterface,
  UnitInfoKwataniScreenInterface,
  UnitInfoLMScreenInterface,
  UnitInfoMobileConeInterface,
  UnitInfoMobileHSIInterface,
  UnitInfoMobileJawInterface,
  UnitInfoMobileVSIInterface,
  UnitInterface,
} from 'store/units/types';
import { PanelType, PortMode } from 'store/units/types';
import { UnitTypes } from 'unitData/types';
import { supportsRiskAssessment } from 'util/unitTypeClassification';
import { getDeckList } from 'unitData/Generic/GenericScreen/GenericScreenData';
import { getDefaultPorts } from 'store/units/setAddUnitPorts';
import { alignmentToLocation } from 'data-migrations/utils/alignmentToLocation';
import { getPortLimits } from 'store/units/unitRegistry';
import { getDefaultHighBinData } from '../../unitData/Crushers/CrusherConeCH/CrusherConeCHData';
import * as Sentry from '@sentry/react';

export const migration0: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    dataVersion: DATA_VERSION.DATA_VERSION1,
  };
  return migrationCopy;
};

export const migration1: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration1:
      'Rev list of over-/undersize options for circular and linear motion screens',
    dataVersion: DATA_VERSION.DATA_VERSION2,
  };
  migrationCopy.state.unitState.units.forEach((element) => {
    if (
      element.type === UnitTypes.LINEAR_MOTION_SCREEN ||
      element.type === UnitTypes.CONVENTIONAL_SCREEN
    ) {
      const unitInfo = element.unitInfo as
        UnitInfoLMScreenInterface | UnitInfoCMScreenInterface;
      Object.values(unitInfo.deckList).forEach((deck: any) => {
        if (deck.maxAmountOfOverUnderSize.name === '10/10') {
          deck.maxAmountOfOverUnderSize.name = 'High';
        } else if (deck.maxAmountOfOverUnderSize.name === '10/15') {
          deck.maxAmountOfOverUnderSize.name = 'Normal';
        } else if (deck.maxAmountOfOverUnderSize.name === '15/20') {
          deck.maxAmountOfOverUnderSize.name = 'Low';
        }
      });
    }
  });
  return migrationCopy;
};

export const migration2: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration2: 'New model names for primary SH feeders',
    dataVersion: DATA_VERSION.DATA_VERSION3,
  };
  migrationCopy.state.unitState.units.forEach((element: any) => {
    if (element.type === UnitTypes.PLATE_FEEDER_SH) {
      if (element.unitInfo.modelName.slice(-1) === '1') {
        const newModelName = element.unitInfo.modelName.slice(0, -1) + '2';
        element.unitInfo.modelName = newModelName;
        element.unitInfo.mining = false;
      } else if (element.unitInfo.modelName.slice(-2) === '1M') {
        const newModelName = element.unitInfo.modelName.slice(0, -2) + '2';
        element.unitInfo.modelName = newModelName;
        element.unitInfo.mining = false;
      } else if (element.unitInfo.modelName.slice(-1) === '5') {
        element.unitInfo.mining = true;
      }
    }
  });
  return migrationCopy;
};

export const migration3: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration3: 'If not present add SPLIT1 to state for MOBILE_TRACKED_SCREEN',
    dataVersion: DATA_VERSION.DATA_VERSION4,
  };
  migrationCopy.state.unitState.units.forEach((element: any) => {
    if (
      element.type === UnitTypes.MOBILE_TRACKED_SCREEN &&
      element.unitInfo.SPLIT1 === undefined
    ) {
      element.unitInfo.SPLIT1 = { amountRight: 100 };
    }
  });
  return migrationCopy;
};

export const migration4: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration4:
      'If type MOBILE_HSI exist replace it with type MOBILE_TRACKED_HSI',
    dataVersion: DATA_VERSION.DATA_VERSION5,
  };
  migrationCopy.state.unitState.units.forEach((element: any) => {
    if (element.type === 'MOBILE_HSI') {
      element.type = UnitTypes.MOBILE_TRACKED_HSI;
      element.layout.category = UnitTypes.MOBILE_TRACKED_HSI;
    }
  });
  return migrationCopy;
};

export const migration5: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration5: 'Changed object keys for multiple units',
    dataVersion: DATA_VERSION.DATA_VERSION6,
  };

  migrationCopy.state.unitState.units.forEach((unit: UnitInterface) => {
    if (unit.type === UnitTypes.BANANA_SCREEN) {
      const unitInfo = unit.unitInfo as UnitInfoBananaScreenInterface;
      // @ts-expect-error - unitName field was renamed modelName on old data
      unitInfo.modelName = unitInfo.unitName;
      // @ts-expect-error - model field removed in migration
      delete unitInfo.model;
    } else if (unit.type === UnitTypes.MOBILE_VSI) {
      const unitInfo = unit.unitInfo as UnitInfoMobileVSIInterface;
      // @ts-expect-error - modelName was object on old data, now string
      unitInfo.modelName = unitInfo.modelName.value ?? unitInfo.modelName;
      unitInfo.display.displayModelName =
        // @ts-expect-error - displayModel field renamed to displayModelName
        unitInfo.display.displayModel ?? unitInfo.display.displayModelName;
      unitInfo.display.displayUnitName =
        // @ts-expect-error - displayUnit field renamed to displayUnitName
        unitInfo.display.displayUnit ?? unitInfo.display.displayUnitName;
    } else if (
      unit.type === UnitTypes.MOBILE_TRACKED_CONE ||
      unit.type === UnitTypes.MOBILE_WHEELED_CONE ||
      unit.type === UnitTypes.MOBILE_TRACKED_SCREEN ||
      unit.type === UnitTypes.MOBILE_WHEELED_SCREEN
    ) {
      const unitInfo = unit.unitInfo as UnitInfoMobileConeInterface;
      unitInfo.modelName = (unitInfo as any).model ?? unitInfo.modelName;
      // delete (unitInfo as any).model;
    }

    if (
      unit.type === UnitTypes.MOBILE_TRACKED_HSI ||
      unit.type === UnitTypes.MOBILE_WHEELED_HSI ||
      unit.type === UnitTypes.MOBILE_JAW_AND_CONE ||
      unit.type === UnitTypes.MOBILE_TRACKED_CONE ||
      unit.type === UnitTypes.MOBILE_WHEELED_CONE ||
      unit.type === UnitTypes.MOBILE_TRACKED_SCREEN ||
      unit.type === UnitTypes.MOBILE_WHEELED_SCREEN
    ) {
      const unitInfo = unit.unitInfo;
      // @ts-expect-error - displayModel field renamed to displayModelName
      unitInfo.display.displayModelName =
        // @ts-expect-error - displayModel field exists on old data
        unitInfo.display.displayModel ?? unitInfo.display.displayModelName;
      // @ts-expect-error - displayUnit field renamed to displayUnitName
      unitInfo.display.displayUnitName =
        // @ts-expect-error - displayUnit field exists on old data
        unitInfo.display.displayUnit ?? unitInfo.display.displayUnitName;

      // @ts-expect-error - displayUnit field removal from old data
      delete unitInfo.display.displayUnit;
      // @ts-expect-error - displayModel field removal from old data
      delete unitInfo.display.displayModel;
    }

    if (unit.type === UnitTypes.GRIZZLY_SCREEN) {
      const unitInfo = unit.unitInfo as UnitInfoGrizzlyScreenSGInterface;

      // @ts-expect-error - deck field was renamed to deckList
      unitInfo.deckList = unitInfo.deck
        ? // @ts-expect-error - deck field exists on old data
          [Object.assign({}, unitInfo.deck)]
        : unitInfo.deckList;
      // @ts-expect-error - deck field removal from old data
      delete unitInfo.deck;
    }

    if (
      unit.type === UnitTypes.CONVENTIONAL_SCREEN ||
      unit.type === UnitTypes.LINEAR_MOTION_SCREEN ||
      unit.type === UnitTypes.SCHENCK_SCREEN ||
      unit.type === UnitTypes.GRIZZLY_SCREEN
    ) {
      // @ts-expect-error - decks field was renamed to nbrOfDecks
      unit.unitInfo.nbrOfDecks =
        // @ts-expect-error - decks field exists on old data
        unit.unitInfo.decks ?? unit.unitInfo.nbrOfDecks;
      // @ts-expect-error - decks field removal from old data
      delete unit.unitInfo.decks;
    }
  });

  return migrationCopy;
};

export const migration6: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration6: 'Change data type for deck lists',
    dataVersion: DATA_VERSION.DATA_VERSION7,
  };
  migrationCopy.state.unitState.units.forEach((unit: UnitInterface) => {
    const info = unit.unitInfo as
      | UnitInfoGrizzlyScreenSGInterface
      | UnitInfoCMScreenInterface
      | UnitInfoLMScreenInterface
      | UnitInfoKwataniScreenInterface;

    if (
      unit.type === UnitTypes.GRIZZLY_SCREEN ||
      unit.type === UnitTypes.CONVENTIONAL_SCREEN ||
      unit.type === UnitTypes.LINEAR_MOTION_SCREEN ||
      unit.type === UnitTypes.KWATANI_SCREEN
    ) {
      info.deckList = (
        unit.unitInfo as UnitInfoGrizzlyScreenSGInterface
      ).deckList;
      if (Array.isArray(info.deckList)) {
        // @ts-expect-error - converting array to object indexed by number
        unit.unitInfo.deckList = info.deckList.reduce((acc, curr, i) => {
          return {
            ...acc,
            [i]: curr,
          };
        }, {});
      }
    }
  });

  return migrationCopy;
};

export const migration7: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration7: 'Change type for CM and LM screen screeningType',
    dataVersion: DATA_VERSION.DATA_VERSION8,
  };

  data.state.unitState.units.forEach((unit) => {
    if (
      unit.type === UnitTypes.LINEAR_MOTION_SCREEN ||
      unit.type === UnitTypes.CONVENTIONAL_SCREEN
    ) {
      type Type = UnitInfoCMScreenInterface | UnitInfoLMScreenInterface;
      const unitInfo = unit.unitInfo as Type;
      unitInfo.wetScreening =
        // @ts-expect-error - screeningType field exists on old data
        unitInfo.screeningType === 'Wet' || !!unitInfo.wetScreening;
      // @ts-expect-error - screeningType field removal from old data
      delete unitInfo.screeningType;
    }
  });

  return migrationCopy;
};

export const migration8: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration8: 'Change data model for generic screen',
    dataVersion: DATA_VERSION.DATA_VERSION9,
  };

  data.state.unitState.units.forEach((unit) => {
    if (unit.type === UnitTypes.GENERIC_SCREEN) {
      const setSepSiz = (index: number) => {
        // @ts-expect-error - updating deckList structure
        unitInfo.deckList[index] = {
          separationSize:
            // @ts-expect-error - separationSize field exists on old data
            unitInfo.separationSize?.[index] ??
            // @ts-expect-error - deckList field structure change
            unitInfo.deckList[index].separationSize,
        };
      };

      const unitInfo = unit.unitInfo as UnitInfoGenericScreenInterface;

      // @ts-expect-error - selectedDeck field exists on old data
      unitInfo.nbrOfDecks = unitInfo.selectedDeck ?? unitInfo.nbrOfDecks;

      unitInfo.deckList = !unitInfo.deckList
        ? getDeckList(unitInfo.modelName)
        : unitInfo.deckList;

      // @ts-expect-error - setSepSiz function called with string index
      Object.keys(unitInfo.deckList).map((i) => setSepSiz(i));

      // @ts-expect-error - selectedDeck field removal from old data
      delete unitInfo.selectedDeck;
      // @ts-expect-error - availableDecks field removal from old data
      delete unitInfo.availableDecks;
      // @ts-expect-error - separationSize field removal from old data
      delete unitInfo.separationSize;
    }
  });

  return migrationCopy;
};

export const migration9: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration9: 'Add ppx display values',
    dataVersion: DATA_VERSION.DATA_VERSION10,
  };

  migrationCopy.state.unitState.units.forEach((unit) => {
    if (
      unit.type !== UnitTypes.FREE_TEXT_BOX &&
      unit.type !== UnitTypes.PRINT_WINDOW
    ) {
      const display = unit.flowProperties.display;
      if (!('displayPxxValue1' in display)) {
        unit.flowProperties.display['displayPxxValue1'] = true;
        unit.flowProperties.display['displayPxxValue2'] = true;
        unit.flowProperties.display['displayPxxValue3'] = true;
      }
    }
  });

  return migrationCopy;
};

export const migration10: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration10: 'Add ports and validate connections',
    dataVersion: DATA_VERSION.DATA_VERSION11,
  };
  const { units, ports } = migrationCopy.state.unitState;
  const portKeys = Object.values(ports)
    .flat()
    .map((port) => port.key);

  const invalidConnections: string[] = [];

  migrationCopy.state.connectionState.connections.forEach((connection) => {
    const { key, from, to, fromPort, toPort } = connection;
    const fromUnitExists = units.find((unit) => unit.key === from);
    const toUnitExists = units.find((unit) => unit.key === to);

    if (!fromUnitExists || !toUnitExists) {
      invalidConnections.push(key);
    }

    const fromPortExists = portKeys.find((key) => key === fromPort);
    const toPortExists = portKeys.find((key) => key === toPort);

    if (!fromPortExists || !toPortExists) {
      invalidConnections.push(key);
    }
  });

  migrationCopy.state.connectionState.connections =
    migrationCopy.state.connectionState.connections.filter(
      (c) => !invalidConnections.includes(c.key),
    );

  // Add default layout to units without one (legacy data?)
  migrationCopy.state.unitState.units.forEach((unit) => {
    if (!unit.layout) {
      const portConfig = getPortLimits(unit.type);
      Sentry.captureMessage('Migration10: Adding default layout to unit', {
        level: 'warning',
        extra: {
          unitKey: unit.key,
          unitType: unit.type,
        },
      });
      unit.layout = {
        category: unit.type,
        loc: '0 0',
        inputPorts: portConfig.minInputPorts,
        outputPorts: portConfig.minOutputPorts,
        mirrored: false,
        hideNonMajorWarnings: false,
      };
    }
  });

  const clamp = (num: number, min: number, max: number) =>
    Math.min(Math.max(num, min), max);

  migrationCopy.state.unitState.units.forEach((unit) => {
    const { maxInputPorts, maxOutputPorts, minInputPorts, minOutputPorts } =
      getPortLimits(unit.type);

    // This solves an issue with corrupted flow sheets that had incorrect number of input/output ports
    unit.layout.inputPorts = clamp(
      unit.layout.inputPorts,
      minInputPorts,
      maxInputPorts,
    );
    unit.layout.outputPorts = clamp(
      unit.layout.outputPorts,
      minOutputPorts,
      maxOutputPorts,
    );

    const { inputPorts, outputPorts } = unit.layout;

    const defaultPorts = getDefaultPorts(unit.key, unit.layout);

    const resInputs = defaultPorts
      .filter((p) => p.portMode === PortMode.INPUT)
      .slice(inputPorts);
    const resOutput = defaultPorts
      .filter((p) => p.portMode === PortMode.OUTPUT)
      .slice(outputPorts);

    [...resInputs, ...resOutput].forEach((port) => {
      if (!ports[unit.key].find((a) => a.portIndex === port.portIndex)) {
        migrationCopy.state.unitState.ports[unit.key] = [
          ...ports[unit.key],
          port,
        ];
      }
    });
  });

  return migrationCopy;
};

export const migration11: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);

  migrationCopy.state = {
    ...migrationCopy.state,
    migration11: 'Migrate old panels location implementation',
    dataVersion: DATA_VERSION.DATA_VERSION12,
  };

  if (migrationCopy.state.unitState.panels) {
    return migrationCopy;
  }

  migrationCopy.state.unitState.panels = {};

  migrationCopy.state.unitState.units.forEach((unit) => {
    const getAlignment =
      unit.type === UnitTypes.PRINT_WINDOW ||
      unit.type === UnitTypes.FREE_TEXT_BOX
        ? () => '0 0'
        : alignmentToLocation(unit);

    migrationCopy.state.unitState.panels = {
      ...migrationCopy.state.unitState.panels,
      [unit.key]: {
        [PanelType.INFO_PANEL]: {
          unitKey: unit.key,
          type: PanelType.INFO_PANEL,
          loc: getAlignment('infoPanelAlignment'),
        },
        [PanelType.WARNINGS_PANEL]: {
          unitKey: unit.key,
          type: PanelType.WARNINGS_PANEL,
          loc: getAlignment('warningsPanelAlignment'),
        },
        [PanelType.FLOW_PROPERTIES_GRAPH_PANEL]: {
          unitKey: unit.key,
          type: PanelType.FLOW_PROPERTIES_GRAPH_PANEL,
          loc: getAlignment('flowPropertiesGraphAlignment'),
          // @ts-expect-error - flowPropertiesGraphWidth field exists on old data
          width: unit.layout.flowPropertiesGraphWidth,
          // @ts-expect-error - flowPropertiesGraphHeight field exists on old data
          height: unit.layout.flowPropertiesGraphHeight,
        },
        [PanelType.FLOW_PROPERTIES_SIZE_TABLE_PANEL]: {
          unitKey: unit.key,
          type: PanelType.FLOW_PROPERTIES_SIZE_TABLE_PANEL,
          loc: getAlignment('flowPropertiesSizeAlignment'),
        },
        [PanelType.FLOW_PROPERTIES_PXX_TABLE_PANEL]: {
          unitKey: unit.key,
          type: PanelType.FLOW_PROPERTIES_PXX_TABLE_PANEL,
          loc: getAlignment('flowPropertiesPxxAlignment'),
        },
        [PanelType.PARTICLE_SIZE_DISTRIBUTION_LIST_PANEL]: {
          unitKey: unit.key,
          type: PanelType.PARTICLE_SIZE_DISTRIBUTION_LIST_PANEL,
          loc: getAlignment('feedTableAlignment'),
        },
        [PanelType.PARTICLE_SIZE_DISTRIBUTION_GRAPH_PANEL]: {
          unitKey: unit.key,
          type: PanelType.PARTICLE_SIZE_DISTRIBUTION_GRAPH_PANEL,
          loc: getAlignment('feedGraphAlignment'),
        },
      },
    };
  });

  return migrationCopy;
};

export const migration12: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration12: 'New model names for Kwatani screens',
    dataVersion: DATA_VERSION.DATA_VERSION13,
  };
  migrationCopy.state.unitState.units.forEach((element: any) => {
    if (element.type === UnitTypes.KWATANI_SCREEN) {
      if (element.unitInfo.modelName === 'KM4281') {
        element.unitInfo.modelName = 'KM4291';
      } else if (element.unitInfo.modelName === 'KM3682') {
        element.unitInfo.modelName = 'KM3692';
      }
    } else if (element.type === UnitTypes.LINEAR_MOTION_SCREEN) {
      if (element.unitInfo.modelName === 'KL3081') {
        element.unitInfo.modelName = 'KL3091';
      } else if (element.unitInfo.modelName === 'KL3681') {
        element.unitInfo.modelName = 'KL3691';
      } else if (element.unitInfo.modelName === 'KL3082') {
        element.unitInfo.modelName = 'KL3092';
      }
    }
  });
  return migrationCopy;
};

export const migration13: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration13: 'Rev list of over-/undersize options for grizzly screens',
    dataVersion: DATA_VERSION.DATA_VERSION14,
  };
  migrationCopy.state.unitState.units.forEach((element: any) => {
    if (element.type === UnitTypes.GRIZZLY_SCREEN) {
      const unitInfo = element.unitInfo as UnitInfoGrizzlyScreenSGInterface;

      Object.values(unitInfo.deckList).forEach((deck: any) => {
        if (deck.maxAmountOfUnderSize.name === '10/10 %') {
          deck.maxAmountOfUnderSize.name = 'High';
        } else if (deck.maxAmountOfUnderSize.name === '10/15 %') {
          deck.maxAmountOfUnderSize.name = 'Normal';
        } else if (deck.maxAmountOfUnderSize.name === '15/20 %') {
          deck.maxAmountOfUnderSize.name = 'Low';
        }
      });
    }
  });
  return migrationCopy;
};

export const migration14: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration14: 'Add attachment tool data to compatible units',
    dataVersion: DATA_VERSION.DATA_VERSION15,
  };
  const applications = {
    [UnitTypes.JAW_CRUSHER_CJ]: BreakerApplication.JAW_CRUSHER,
    [UnitTypes.GYRATORY_CRUSHER_CG]: BreakerApplication.GYRATORY,
  };
  migrationCopy.state.unitState.units.forEach((unit) => {
    if (
      unit.type === UnitTypes.JAW_CRUSHER_CJ ||
      unit.type === UnitTypes.GYRATORY_CRUSHER_CG
    ) {
      ((unit.unitInfo as any).attachmentTool as AttachementTool) = {
        boom: undefined,
        breaker: undefined,
        powerPack: undefined,
        enabled: false,
        application: applications[unit.type],
      };
    }
  });
  return migrationCopy;
};

export const migration15: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration15: 'Adds fields to free text box',
    dataVersion: DATA_VERSION.DATA_VERSION16,
  };

  migrationCopy.state.unitState.units.forEach((unit) => {
    if (unit.type === UnitTypes.FREE_TEXT_BOX) {
      const unitInfo = unit.unitInfo as any as UnitInfoFreeTextBoxInterface;
      unitInfo.textAlignment = 'center';
      unitInfo.textWeight = 'regular';
      unitInfo.textColor = unitInfo.textColor.toUpperCase();
      unitInfo.color = unitInfo.color.toUpperCase();
    }
  });

  return migrationCopy;
};

export const migration16: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration16: 'Add moistureUI to unitinfo for FEED',
    dataVersion: DATA_VERSION.DATA_VERSION17,
  };

  data.state.unitState.units.forEach((unit) => {
    if (unit.type === UnitTypes.FEED) {
      type Type = UnitInfoFeedInterface;
      const unitInfo = unit.unitInfo as Type;
      unitInfo.moistureUI ??= (unit as any).inputFlow?.moisture ?? 0;
    }
  });

  return migrationCopy;
};

export const migration17: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration17: 'Add deck 3 for FF screen for Mobile Jaw',
    dataVersion: DATA_VERSION.DATA_VERSION18,
  };

  data.state.unitState.units.forEach((unit) => {
    if (
      unit.type === UnitTypes.MOBILE_WHEELED_JAW ||
      unit.type === UnitTypes.MOBILE_TRACKED_JAW
    ) {
      type Type = UnitInfoMobileJawInterface;
      const unitInfo = unit.unitInfo as Type;
      if (
        unitInfo.FREEFALL_SCREEN1 &&
        unitInfo.FREEFALL_SCREEN1.decks[2] === undefined
      ) {
        unitInfo.FREEFALL_SCREEN1.decks[2] = { separationSize: undefined };
      }
    }
  });

  return migrationCopy;
};

export const migration18: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration18: 'Add CrusherHighBinInterface to UnitInfoCrusherInterface',
    dataVersion: DATA_VERSION.DATA_VERSION19,
  };

  data.state.unitState.units.forEach((unit) => {
    if (
      unit.type === UnitTypes.CONE_CRUSHER_CH ||
      unit.type === UnitTypes.CONE_CRUSHER_CS
    ) {
      type Type = UnitInfoCrusherInterface;
      const unitInfo = unit.unitInfo as Type;
      unitInfo.highBin ??= getDefaultHighBinData();
    }
  });

  return migrationCopy;
};

export const migration19: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration19: 'Add modelFamily to unitinfo for LINEAR_MOTION_SCREEN',
    dataVersion: DATA_VERSION.DATA_VERSION20,
  };

  data.state.unitState.units.forEach((unit) => {
    if (unit.type === UnitTypes.LINEAR_MOTION_SCREEN) {
      type Type = UnitInfoLMScreenInterface;
      const unitInfo = unit.unitInfo as Type;
      unitInfo.modelFamily ??= 'SL';
    }
  });

  return migrationCopy;
};

export const migration20: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration20:
      'Restructure Generic and Grizzly screens - rename display properties, move to deck level, and standardize architecture',
    dataVersion: DATA_VERSION.DATA_VERSION21,
  };

  data.state.unitState.units.forEach((unit) => {
    if (unit.type === UnitTypes.GENERIC_SCREEN) {
      const unitInfo = unit.unitInfo as UnitInfoGenericScreenInterface;

      // Step 1: Rename selectedDeck to displayNumberOfDecks
      // @ts-expect-error - selectedDeck field exists on old data
      if (unitInfo.display.selectedDeck !== undefined) {
        // @ts-expect-error - assigning selectedDeck to displayNumberOfDecks
        unitInfo.display.displayNumberOfDecks = unitInfo.display.selectedDeck;
        // @ts-expect-error - selectedDeck field removal from old data
        delete unitInfo.display.selectedDeck;
      }

      // Step 2: Move display.separationSize array to deck-level display objects
      // @ts-expect-error - separationSize field exists on old data
      const separationSizeDisplay = unitInfo.display.separationSize ?? [];

      // Migrate display flags from unitInfo to each deck
      Object.keys(unitInfo.deckList).forEach((deckIndex) => {
        const i = parseInt(deckIndex);
        const deck = unitInfo.deckList[i as keyof typeof unitInfo.deckList];
        deck.display = {
          separationSize: separationSizeDisplay[i] ?? true,
        };
      });

      delete (unitInfo.display as any).separationSize;
    } else if (unit.type === UnitTypes.GRIZZLY_SCREEN) {
      const unitInfo = unit.unitInfo as UnitInfoGrizzlyScreenSGInterface;

      // Get all old property values
      // @ts-expect-error - grizzlySeparation field exists on old data
      const grizzlySeparation = unitInfo.grizzlySeparation ?? 100;
      const displayGrizzlySeparation =
        // @ts-expect-error - displayGrizzlySeparation field exists on old data
        unitInfo.display.displayGrizzlySeparation ?? true;
      const displayScreeningElement =
        // @ts-expect-error - displayDeckScreeningElement field exists on old data
        unitInfo.display.displayDeckScreeningElement ?? true;

      const displayDeckSeparation =
        (unitInfo.display as any).displayDeckSeparation ?? true;
      const oldSeparationDeck = (unitInfo.deckList as any)[0];

      // Create new deckList structure - both decks now use the same full interface
      const newDeckList: any = {
        // Deck 1 (index 0): Grizzly deck - now uses full interface like other screens
        0: {
          separationSize: grizzlySeparation,
          // Initialize with default values for properties only used by separation deck
          screeningElement: oldSeparationDeck?.screeningElement ?? {
            name: '',
            value: '',
          },
          efficiencyScreeningElement:
            oldSeparationDeck?.efficiencyScreeningElement ?? {
              name: '',
              value: '',
            },
          maxAmountOfUnderSize: oldSeparationDeck?.maxAmountOfUnderSize ?? {
            name: '',
            value: '',
          },
          openArea: 0,
          particleShape: oldSeparationDeck?.particleShape ?? {
            name: '',
            value: '',
          },
          efficiency: oldSeparationDeck?.efficiency ?? { name: '', value: '' },
          efficiencyNumber: 0,
          display: {
            separationSize: displayGrizzlySeparation,
            screeningElement: false, // Hidden for grizzly deck
          },
        },
      };

      // If nbrOfDecks === 2, move the old separation deck to index 1 with display flags
      if (unitInfo.nbrOfDecks === 2 && oldSeparationDeck) {
        oldSeparationDeck.display ??= {
          separationSize: displayDeckSeparation,
          screeningElement: displayScreeningElement,
        };
        oldSeparationDeck.display.separationSize = displayDeckSeparation;
        oldSeparationDeck.display.screeningElement = displayScreeningElement;

        newDeckList[1] = oldSeparationDeck;
      }

      unitInfo.deckList = newDeckList;

      // @ts-expect-error - grizzlySeparation field removal from old data
      delete unitInfo.grizzlySeparation;
      // @ts-expect-error - displayGrizzlySeparation field removal from old data
      delete unitInfo.display.displayGrizzlySeparation;
      // @ts-expect-error - displayDeckScreeningElement field removal from old data
      delete unitInfo.display.displayDeckScreeningElement;
      // @ts-expect-error - displayDeckSeparation field removal from old data
      delete unitInfo.display.displayDeckSeparation;
    }
  });

  return migrationCopy;
};

export const migration21: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration21: 'Change model names for generic crusher and screen',
    dataVersion: DATA_VERSION.DATA_VERSION22,
  };

  data.state.unitState.units.forEach((unit) => {
    if (unit.type === UnitTypes.GENERIC_SCREEN) {
      const unitInfo = unit.unitInfo as UnitInfoGenericScreenInterface;

      if (
        unitInfo.modelName === 'Inclined' ||
        unitInfo.modelName === 'Horizontal' ||
        unitInfo.modelName === 'Scalping' ||
        unitInfo.modelName === 'Multislope' ||
        unitInfo.modelName === 'Grizzly'
      ) {
        unitInfo.modelName = unitInfo.modelName + ' screen';
      }
    } else if (unit.type === UnitTypes.GENERIC_CRUSHER) {
      const unitInfo = unit.unitInfo as UnitInfoGenericCrusherInterface;

      if (
        unitInfo.modelName === 'VSI' ||
        unitInfo.modelName === 'HSI' ||
        unitInfo.modelName === 'Hybrid'
      ) {
        unitInfo.modelName = unitInfo.modelName + ' crusher';
      }
      if (
        unitInfo.modelName === 'Cone Crusher' ||
        unitInfo.modelName === 'Gyratory Crusher' ||
        unitInfo.modelName === 'Jaw Crusher'
      ) {
        unitInfo.modelName = unitInfo.modelName.replace('Crusher', 'crusher');
      }
    }
  });

  return migrationCopy;
};

export const migration22: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration22: 'Add display object to grizzly feeder decks',
    dataVersion: DATA_VERSION.DATA_VERSION23,
  };

  migrationCopy.state.unitState.units.forEach((unit) => {
    if (
      unit.type === UnitTypes.GRIZZLY_FEEDER_SW ||
      unit.type === UnitTypes.GRIZZLY_FEEDER_SV ||
      unit.type === UnitTypes.GRIZZLY_FEEDER_SV_E
    ) {
      const unitInfo = unit.unitInfo as any;

      // Add display object to deck 0 if it doesn't exist
      if (unitInfo.decks?.[0]) {
        unitInfo.decks[0].display ??= {
          separationSize: unitInfo.display?.displaySepSizeDeck1 ?? false,
        };
      }

      // Add display object to deck 1 if it exists and doesn't have display
      if (unitInfo.decks?.[1]) {
        unitInfo.decks[1].display ??= {
          separationSize: unitInfo.display?.displaySepSizeDeck2 ?? false,
        };
      }
    }
  });

  return migrationCopy;
};

export const migration23: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration23: 'Rename discontinued UJ473 configurations',
    dataVersion: DATA_VERSION.DATA_VERSION24,
  };

  // Rename discontinued configurations for Mobile Jaw UJ473
  data.state.unitState.units.forEach((unit) => {
    if (unit.type === UnitTypes.MOBILE_WHEELED_JAW) {
      type Type = UnitInfoMobileJawInterface;
      const unitInfo = unit.unitInfo as Type;
      if (
        unitInfo.modelName === 'UJ473' &&
        unitInfo.configuration.displayName ===
          'Take out natural fines as separate product'
      ) {
        unitInfo.configuration.displayName =
          'Take out natural fines as separate product *DISCONTINUED*';
      } else if (
        unitInfo.modelName === 'UJ473' &&
        unitInfo.configuration.displayName ===
          'Take out natural fines, bypass pre-screen throughput'
      ) {
        unitInfo.configuration.displayName =
          'Take out natural fines, bypass pre-screen throughput *DISCONTINUED*';
      }
    }
  });

  return migrationCopy;
};

export const migration24: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration24: 'Move FEED inputFlow to unitInfo',
    dataVersion: DATA_VERSION.DATA_VERSION25,
  };

  // Move FEED inputFlow into unitInfo for consistency with new architecture
  migrationCopy.state.unitState.units.forEach((unit) => {
    if (unit.type === UnitTypes.FEED) {
      const feedInfo = unit.unitInfo as UnitInfoFeedInterface;
      const oldInputFlow = (unit as any).inputFlow;

      if (oldInputFlow && !feedInfo.inputFlow) {
        feedInfo.inputFlow = oldInputFlow;
      }

      if ((unit as any).inputFlow) {
        delete (unit as any).inputFlow;
      }
    }
  });

  return migrationCopy;
};

export const migration26: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration26: 'Enable showRiskLevel by default for existing files',
    dataVersion: DATA_VERSION.DATA_VERSION27,
    settingState: {
      ...migrationCopy.state.settingState,
      unitDisplay: {
        ...migrationCopy.state.settingState?.unitDisplay,
        showRiskLevel: true,
      },
    },
  };
  return migrationCopy;
};

export const migration25: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration25: 'Add displayRiskLevel to crushers and screens',
    dataVersion: DATA_VERSION.DATA_VERSION26,
    unitState: {
      ...migrationCopy.state.unitState,
      units: migrationCopy.state.unitState.units.map((unit) => {
        if (supportsRiskAssessment(unit.type)) {
          const unitInfo = unit.unitInfo as any;
          if (
            unitInfo.display &&
            unitInfo.display.displayRiskLevel === undefined
          ) {
            return {
              ...unit,
              unitInfo: {
                ...unitInfo,
                display: {
                  ...unitInfo.display,
                  displayRiskLevel: false,
                },
              },
            };
          }
        }
        return unit;
      }),
    },
  };

  return migrationCopy;
};

export const migration27: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  const simulationResults = {
    ...(migrationCopy.state.unitState.simulationResults || {}),
  };
  const chamberAnalysisResults = {
    ...(migrationCopy.state.unitState.chamberAnalysisResults || {}),
  };

  migrationCopy.state.unitState.units = migrationCopy.state.unitState.units.map(
    (unit) => {
      const legacySimulationResult = (unit as any).simulationResult;
      const legacyChamberAnalysisResult = (unit as any).chamberAnalysisResult;
      const migratedUnit = { ...unit };

      if (legacySimulationResult && !simulationResults[unit.key]) {
        simulationResults[unit.key] = {
          warnings: legacySimulationResult.warnings,
          simulationUnitInfo: legacySimulationResult.simulationUnitInfo,
          reportInfo: legacySimulationResult.reportInfo,
          riskAssessmentInfo: legacySimulationResult.riskAssessmentInfo,
        };
      }

      if (legacyChamberAnalysisResult && !chamberAnalysisResults[unit.key]) {
        chamberAnalysisResults[unit.key] = legacyChamberAnalysisResult;
      }

      if ('simulationResult' in migratedUnit) {
        delete (migratedUnit as any).simulationResult;
      }

      if ('chamberAnalysisResult' in migratedUnit) {
        delete (migratedUnit as any).chamberAnalysisResult;
      }

      return migratedUnit;
    },
  );

  migrationCopy.state = {
    ...migrationCopy.state,
    migration27:
      'Move legacy unit simulation and chamber analysis results to unitState maps',
    dataVersion: DATA_VERSION.DATA_VERSION28,
    unitState: {
      ...migrationCopy.state.unitState,
      simulationResults,
      chamberAnalysisResults,
    },
  };

  return migrationCopy;
};

const multisizeLinearMotionScreenModels = new Set([
  'SL2451',
  'SL2452',
  'SL2472',
  'SL3051',
  'SL3071',
  'SL3072',
  'SL3672',
  'SA0921',
  'SA1841',
  'SA2451',
]);

const getLegacyLinearMotionScreenSizeOption = (
  modelName: string,
  variantSuffix: string,
) => {
  const mergedModelName = modelName.slice(0, -1);
  if (
    variantSuffix === 'M' &&
    multisizeLinearMotionScreenModels.has(mergedModelName)
  ) {
    return '1';
  }

  return '0';
};

export const migration28: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration28:
      'Screen multisize: replace SL/SA sub-family boolean filters with explicit screenFamily + sizeOption',
    dataVersion: DATA_VERSION.DATA_VERSION29,
  };

  data.state.unitState.units.forEach((unit) => {
    if (unit.type === UnitTypes.LINEAR_MOTION_SCREEN) {
      const unitInfo = unit.unitInfo as unknown as Record<string, unknown>;
      const name =
        typeof unitInfo.modelName === 'string' ? unitInfo.modelName : '';
      const lastChar = name.charAt(name.length - 1);
      const hasVariantSuffix = lastChar === 'K' || lastChar === 'M';
      const isSA = name.charAt(1) === 'A';

      unitInfo.screenFamily = isSA
        ? 'SA_LM'
        : hasVariantSuffix
          ? 'SL_EXCITER'
          : 'SL';
      if (hasVariantSuffix) {
        unitInfo.sizeOption = getLegacyLinearMotionScreenSizeOption(
          name,
          lastChar,
        );
        unitInfo.modelName = name.slice(0, -1);
      } else if (unitInfo.sizeOption === undefined) {
        unitInfo.sizeOption = '0';
      }
      unitInfo.modelFamily = isSA ? 'SA' : 'SL';

      delete unitInfo.sl;
      delete unitInfo.slk;
      delete unitInfo.slm;
      delete unitInfo.sak;
      delete unitInfo.sam;
    }

    if (unit.type === UnitTypes.MULTISLOPE_SCREEN) {
      const unitInfo = unit.unitInfo as unknown as Record<string, unknown>;
      if (unitInfo.sizeOption === undefined) {
        unitInfo.sizeOption = '0';
      }
      delete unitInfo.smk;
      delete unitInfo.smm;
    }
  });

  return migrationCopy;
};

const defaultGrizzlyDeck2ScreeningElement = {
  name: 'WX1000 (Wire mesh)',
  value: 'WX1000_WIRE_MESH',
};

const renamedGrizzlyDeck2ScreeningElement = {
  name: 'WX6000 (Crosscord)',
  value: 'WX6000',
};

export const migration29: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration29:
      'Grizzly screen: replace removed deck 2 screening media with supported defaults',
    dataVersion: DATA_VERSION.DATA_VERSION30,
  };

  data.state.unitState.units.forEach((unit) => {
    if (unit.type !== UnitTypes.GRIZZLY_SCREEN) {
      return;
    }

    const unitInfo = unit.unitInfo as UnitInfoGrizzlyScreenSGInterface;
    const deck2 = unitInfo.deckList?.[1];
    const screeningElement = deck2?.screeningElement;
    const screeningElementName = screeningElement?.name;

    if (!deck2 || !screeningElementName) {
      return;
    }

    if (screeningElementName === 'Crosscord') {
      deck2.screeningElement = renamedGrizzlyDeck2ScreeningElement;
    } else if (!screeningElementName.startsWith('WX')) {
      deck2.screeningElement = defaultGrizzlyDeck2ScreeningElement;
    }
  });

  return migrationCopy;
};

const legacyMobileConeScreenModels: Record<string, string> = {
  SS1443: 'SS1433',
  SS1823: 'SS1823',
};

const legacyMobileHsiCrusherModels: Record<string, string> = {
  CI611: 'CI611',
  CI612: 'CI612',
  CI621: 'CI621',
  CI622: 'CI622',
  CI651: 'CI651',
  CI652: 'CI652',
  CI721: 'CI721',
  CI722: 'CI722',
};

const legacyMobileVsiCrusherModels: Record<string, string> = {
  CV217: 'CV217',
  CV218: 'CV218',
};

const toSimulationModel = (
  savedModel: string,
  legacyModels: Record<string, string>,
) => legacyModels[savedModel] ?? savedModel;

export const migration30: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration30:
      'Mobile cone, HSI, and VSI crusher selections: persist simulation model keys',
    dataVersion: DATA_VERSION.DATA_VERSION31,
  };

  migrationCopy.state.unitState.units.forEach((unit) => {
    if (
      unit.type === UnitTypes.MOBILE_TRACKED_CONE ||
      unit.type === UnitTypes.MOBILE_WHEELED_CONE
    ) {
      const unitInfo = unit.unitInfo as UnitInfoMobileConeInterface;
      const savedModel = unitInfo.feederScreen?.screenModel;
      if (typeof savedModel === 'string') {
        unitInfo.feederScreen.screenModel = toSimulationModel(
          savedModel,
          legacyMobileConeScreenModels,
        );
      }
    } else if (
      unit.type === UnitTypes.MOBILE_TRACKED_HSI ||
      unit.type === UnitTypes.MOBILE_WHEELED_HSI
    ) {
      const unitInfo = unit.unitInfo as UnitInfoMobileHSIInterface;
      const savedModel = unitInfo.HSI1?.modelName;
      if (typeof savedModel === 'string') {
        unitInfo.HSI1.modelName = toSimulationModel(
          savedModel,
          legacyMobileHsiCrusherModels,
        );
      }
    } else if (unit.type === UnitTypes.MOBILE_VSI) {
      const unitInfo = unit.unitInfo as UnitInfoMobileVSIInterface;
      const savedModel = unitInfo.VSI1?.modelName;
      if (typeof savedModel === 'string') {
        unitInfo.VSI1.modelName = toSimulationModel(
          savedModel,
          legacyMobileVsiCrusherModels,
        );
      }
    }
  });

  return migrationCopy;
};

const legacyFeedMaterialIds: Record<string, string> = {
  Granite: 'GRANITE',
  Amphibolite: 'AMPHIBOLITE',
  Andesite: 'ANDESITE',
  Basalt: 'BASALT',
  Diabase: 'DIABASE',
  Diorite: 'DIORITE',
  Dolomite: 'DOLOMITE',
  Gabbro: 'GABBRO',
  Gneiss: 'GNEISS',
  Greywacke: 'GREYWACKE',
  Gritstone: 'GRITSTONE',
  Hornfels: 'HORNFELS',
  Limestone: 'LIMESTONE',
  Marble: 'MARBLE',
  Porphyry: 'PORPHYRY',
  Quartzite: 'QUARTZITE',
  Sandstone: 'SANDSTONE',
  Syenite: 'SYENITE',
  'Iron Ore (M)': 'IRON_ORE_M',
  'Iron Ore (H)': 'IRON_ORE_H',
  Kimberlite: 'KIMBERLITE',
};

const legacyFeedPsdIds: Record<string, string> = {
  'Gravel fine': 'GRAVEL_FINE',
  'Gravel average': 'GRAVEL_AVERAGE',
  'Gravel coarse': 'GRAVEL_COARSE',
  'Gravel extra coarse': 'GRAVEL_EXTRA_COARSE',
  'Scandinavian gravel fine': 'SCANDINAVIAN_GRAVEL_FINE',
  'Scandinavian gravel ave.': 'SCANDINAVIAN_GRAVEL_AVE',
  'Scandinavian gravel coarse': 'SCANDINAVIAN_GRAVEL_COARSE',
  'Ripped rock': 'RIPPED_ROCK',
  'Blasted rock very fine': 'BLASTED_ROCK_VERY_FINE',
  'Blasted rock fine': 'BLASTED_ROCK_FINE',
  'Blasted rock ave.': 'BLASTED_ROCK_AVE',
  'Blasted rock coarse': 'BLASTED_ROCK_COARSE',
  'Blasted rock extra coarse': 'BLASTED_ROCK_EXTRA_COARSE',
  'Blasted rock Mining coarse': 'BLASTED_ROCK_MINING_COARSE',
  'Blasted rock Mining medium': 'BLASTED_ROCK_MINING_MEDIUM',
  'Blasted rock Mining fine': 'BLASTED_ROCK_MINING_FINE',
  'Blasted limestone fine': 'BLASTED_LIMESTONE_FINE',
  'Blasted limestone ave.': 'BLASTED_LIMESTONE_AVE',
  'Blasted limestone coarse': 'BLASTED_LIMESTONE_COARSE',
  Rubble: 'RUBBLE',
};

const legacyMobileJawOptionIds: Record<string, string> = {
  'Tapered grizzly 35-55 mm': 'TAPERED_GRIZZLY_35_55',
  'Tapered grizzly 35-70 mm': 'TAPERED_GRIZZLY_35_70',
  'Tapered grizzly 50-65 mm': 'TAPERED_GRIZZLY_50_65',
  'Tapered grizzly 45-55 mm': 'TAPERED_GRIZZLY_45_55',
  'Tapered grizzly 35-40 mm': 'TAPERED_GRIZZLY_35_40',
  'Tapered grizzly 55-80 mm': 'TAPERED_GRIZZLY_55_80',
  'Tapered grizzly 35-75 mm': 'TAPERED_GRIZZLY_35_75',
  'Tapered grizzly 50-70 mm': 'TAPERED_GRIZZLY_50_70',
  'Tapered grizzly 65-85 mm': 'TAPERED_GRIZZLY_65_85',
  'Straight grizzly 25 mm': 'STRAIGHT_GRIZZLY_25',
  'Straight grizzly 50 mm': 'STRAIGHT_GRIZZLY_50',
  'Straight grizzly 70 mm': 'STRAIGHT_GRIZZLY_70',
  'Straight grizzly 80 mm': 'STRAIGHT_GRIZZLY_80',
  'Punched plate 40 mm': 'PUNCHED_PLATE_40',
  'Punched plate 45 mm': 'PUNCHED_PLATE_45',
  'Punched plate 50 mm': 'PUNCHED_PLATE_50',
  'Punched plate 60 mm': 'PUNCHED_PLATE_60',
  'Punched plate 65 mm': 'PUNCHED_PLATE_65',
  'Punched plate 75 mm': 'PUNCHED_PLATE_75',
  'Punched plate 80 mm': 'PUNCHED_PLATE_80',
  'Wire, Square holes': 'WIRE_SQUARE',
  Blanked: 'BLANKED',
};

const migrateLegacyOption = (
  value: string | undefined,
  mappings: Record<string, string>,
) => (value === undefined ? undefined : (mappings[value] ?? value));

export const migration31: MigrationFunction = (data: MigrationObject) => {
  const migrationCopy = Object.assign({}, data);
  migrationCopy.state = {
    ...migrationCopy.state,
    migration31:
      'Feed, mobile jaw, and mobile VSI selections: persist stable catalog identifiers',
    dataVersion: DATA_VERSION.DATA_VERSION32,
  };

  migrationCopy.state.unitState.units.forEach((unit) => {
    if (unit.type === UnitTypes.FEED) {
      const inputFlow = (unit.unitInfo as UnitInfoFeedInterface).inputFlow;
      if (inputFlow.materialName !== 'Custom') {
        const materialId = legacyFeedMaterialIds[inputFlow.materialName];
        if (materialId) inputFlow.materialId = materialId;
      }
      if (inputFlow.psdName !== 'Custom') {
        const psdId = legacyFeedPsdIds[inputFlow.psdName];
        if (psdId) inputFlow.psdId = psdId;
      }
    } else if (
      unit.type === UnitTypes.MOBILE_TRACKED_JAW ||
      unit.type === UnitTypes.MOBILE_WHEELED_JAW
    ) {
      const unitInfo = unit.unitInfo as UnitInfoMobileJawInterface;
      unitInfo.GRIZZLY_FEEDER1.decks[0].grizzlyType = migrateLegacyOption(
        unitInfo.GRIZZLY_FEEDER1.decks[0].grizzlyType,
        legacyMobileJawOptionIds,
      );
      const feederScreen = unitInfo.FEEDER_SCREEN1;
      if (feederScreen) {
        feederScreen.decks[0].screeningMedia = migrateLegacyOption(
          feederScreen.decks[0].screeningMedia,
          legacyMobileJawOptionIds,
        );
        feederScreen.decks[1].screeningMedia = migrateLegacyOption(
          feederScreen.decks[1].screeningMedia,
          legacyMobileJawOptionIds,
        );
      }
    }
  });

  return migrationCopy;
};
